import { cn } from '@/lib/cn';
import { For, type JSX, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { Icon, type IconName } from './Icon';

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  /** Leading icon; give every item in a menu one so rows align. */
  icon?: IconName;
  /** Renders the row unclickable; pair with `title` to say why. */
  disabled?: boolean;
  /** Tooltip (native title attribute). */
  title?: string;
}

export interface MenuProps {
  items: readonly MenuItem[];
  class?: string;
  /** Trigger content replacing the default "⋯" (e.g. a label + chevron). */
  trigger?: JSX.Element;
  /** Trigger classes replacing the default ghost style (e.g. buttonClass('primary')). */
  triggerClass?: string;
}

export const Menu = (props: MenuProps) => {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<DOMRect | null>(null);
  let triggerRef: HTMLButtonElement | undefined;
  let panelRef: HTMLDivElement | undefined;

  const toggle = () => {
    if (open()) {
      setOpen(false);
      return;
    }
    setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    setOpen(true);
  };

  createEffect(() => {
    if (!open()) return;
    const update = () => setAnchor(triggerRef?.getBoundingClientRect() ?? null);
    const onDocumentClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef?.contains(t) || panelRef?.contains(t)) return;
      setOpen(false);
    };
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);
    onCleanup(() => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeydown);
    });
  });

  // Portal + fixed positioning so the panel escapes overflow containers
  // (e.g. the table's overflow-x-auto wrapper). Right-aligned to the trigger.
  const panelStyle = (): JSX.CSSProperties => {
    const rect = anchor();
    if (!rect) return {};
    // Triggers in the lower half (e.g. a bottom action bar) open upward.
    const up = rect.top > window.innerHeight / 2;
    return {
      position: 'fixed',
      ...(up
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
      right: `${window.innerWidth - rect.right}px`,
      'z-index': 60,
    };
  };

  return (
    <div class={cn('relative inline-block', props.class)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open()}
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        class={
          props.triggerClass ??
          'rounded-lg px-2 py-1 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
        }
      >
        {props.trigger ?? <span aria-hidden>⋯</span>}
      </button>
      <Show when={open()}>
        <Portal>
          <div
            ref={panelRef}
            role="menu"
            style={panelStyle()}
            class="min-w-36 rounded-lg border border-neutral-200 bg-surface py-1 shadow-lg dark:border-neutral-700 dark:bg-surface-dark"
          >
            <For each={props.items}>
              {(item) => (
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  title={item.title}
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  class={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800',
                    // Softer red in dark mode — full red-600 glares on the dark panel.
                    item.danger
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-neutral-700 dark:text-neutral-200',
                    item.disabled &&
                      'cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent',
                  )}
                >
                  <Show when={item.icon}>{(name) => <Icon name={name()} />}</Show>
                  {item.label}
                </button>
              )}
            </For>
          </div>
        </Portal>
      </Show>
    </div>
  );
};
